import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import ms from 'ms'
import {
  GetQuoteArgs,
  INTERNAL_ROUTER_PREFERENCE_PRICE,
  KyberBuildResponse,
  KyberRouteResponse,
  QuoteIntent,
  QuoteState,
  RouterPreference,
  TradeResult,
} from 'state/routing/types'
import { isExactInput, tranformKyberQuoteToTrade, } from 'state/routing/utils'
import { logSwapQuoteRequest } from 'tracing/swapFlowLoggers'
import { trace } from 'tracing/trace'
import { InterfaceEventNameLocal } from 'uniswap/src/features/telemetry/constants'
import { sendAnalyticsEvent } from 'uniswap/src/features/telemetry/send'
import { isAddress } from 'utilities/src/addresses'
import { useKyberChain } from 'constants/chains'

const KYBERSWAP_AGGREGATOR_DNS_URL = process.env.REACT_APP_KYBERSWAP_AGGREGATOR
if (KYBERSWAP_AGGREGATOR_DNS_URL === undefined) {
  throw new Error(`KYBERSWAP_AGGREGATOR_DNS_URL must be defined environment variables`)
}



export const kyberRoutingApi = createApi({
    reducerPath: 'kyberApi',
    baseQuery: fetchBaseQuery(),
    endpoints: (build) => ({
        getKyberQuote: build.query<TradeResult, GetQuoteArgs>({
          queryFn(args, _api, _extraOptions, fetch) {
            return trace({ name: 'KyberQuote', op: 'quote.kyber_route', data: {...args} }, async (trace) => {
              logSwapQuoteRequest(args.tokenInChainId, RouterPreference.API, false)
                const {
                    tokenInAddress: tokenIn,
                    tokenInChainId,
                    tokenOutAddress: tokenOut,
                    tokenOutChainId,
                    amount,
                    tradeType,
                    sendPortionEnabled,
                } = args

                let TokenIn = tokenIn
                if (!isAddress(tokenIn)){
                  TokenIn = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
                }
                let TokenOut = tokenOut
                if (!isAddress(TokenOut)){
                  TokenOut = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
                }
                const requestBody = {
                    tokenInChainId,
                    TokenIn,
                    tokenOutChainId,
                    TokenOut,
                    amount,
                    sendPortionEnabled,
                    type: isExactInput(tradeType) ? 'currency_in' : '',
                    intent:
                    args.routerPreference === INTERNAL_ROUTER_PREFERENCE_PRICE ? QuoteIntent.Pricing : QuoteIntent.Quote,
                    swapper: args.account,
                }
            
              const chainName = useKyberChain(requestBody.tokenInChainId)
              if (chainName == undefined) {
                throw new Error (`Unsupport chainId: ${requestBody.tokenInChainId}`)
              }
              
              const response = await fetch({
                method: 'GET',
                url: `${KYBERSWAP_AGGREGATOR_DNS_URL}/${chainName}/api/v1/routes?tokenIn=${requestBody.TokenIn}&tokenOut=${requestBody.TokenOut}&amountIn=${amount}&gasInclude=true`,
                headers: {
                  'Accept-Version': 'Latest', 
                  'Accept': 'application/json'
                },
              })

              if (response.error) {
                // cast as any here because we do a runtime check on it being an object before indexing into .errorCode
                const errorData = response.error.data as { code?: number; message?: string; requestId?: string }
                // NO_ROUTE should be treated as a valid response to prevent retries.
                if (
                  typeof errorData === 'object' &&
                  ( errorData?.code === 4000 )
                ) {
                  sendAnalyticsEvent(InterfaceEventNameLocal.NoQuoteReceivedFromKyberRoutingAPI, {
                    requestBody,
                    response,
                    routerPreference: 'api'
                  })
                  return {
                    data: { state: QuoteState.NOT_FOUND, latencyMs: trace.now() },
                  }
                } else {
                  trace.setError(response.error)
                  return { error: response.error }
                }
              }

              const kyberRouteResponse = response.data as KyberRouteResponse
              const responseBody = {
                routeSummary: kyberRouteResponse.data.routeSummary,
                sender: requestBody.swapper,
                recipient: requestBody.swapper,
                slippageTolerance: 50 //0.1%
              }

              const responsePost = await fetch({
                method: 'POST',
                url: `${KYBERSWAP_AGGREGATOR_DNS_URL}/${chainName}/api/v1/route/build`,
                headers: {
                  "Content-Type": "application/json"
                },
                body: responseBody,
              })

              if (responsePost.error) {
                // cast as any here because we do a runtime check on it being an object before indexing into .errorCode
                const errorData = responsePost.error.data as { code?: number; message?: string; requestId?: string }
                // NO_ROUTE should be treated as a valid response to prevent retries.
                if (
                  typeof errorData === 'object' &&
                  ( errorData?.code === 4000 )
                ) {
                  sendAnalyticsEvent(InterfaceEventNameLocal.NoQuoteReceivedFromKyberRoutingAPI, {
                    requestBody,
                    response,
                    routerPreference: 'api'
                  })
                  return {
                    data: { state: QuoteState.NOT_FOUND, latencyMs: trace.now() },
                  }
                } else {
                  trace.setError(responsePost.error)
                  return { error: responsePost.error }
                }
              }

              const kyberBuildResponse = responsePost.data as KyberBuildResponse

              
              // console.log('kyberRouteResponse',kyberRouteResponse)
              console.log('kyberBuildResponse',kyberBuildResponse)
              const kyberTrade = await tranformKyberQuoteToTrade(args, kyberBuildResponse)
              return { data: { ...kyberTrade, latencyMs: trace.now(), }, }
            })
          },
          keepUnusedDataFor: ms(`10s`),
          extraOptions: {
            maxRetries: 0,
          },
        }),
      }),
})

export const { useGetKyberQuoteQuery } = kyberRoutingApi
export const useGetKyberRouteQueryState = kyberRoutingApi.endpoints.getKyberQuote.useQueryState